import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";

export default function Analytics() {
  const [data, setData] = useState({ totals: {}, dailyStats: [], topUsers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get("/admin/analytics/messages");
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      }
      setLoading(false);
    };
    fetchAnalytics();
  }, []);

  const { totals, dailyStats, topUsers } = data;
  // Success rate should use delivered+read only.
  const total = Number(totals.total || 0);
  const deliveredRead = Number(totals.delivered_read || 0) || (Number(totals.delivered || 0) + Number(totals.read_count || 0));
  const successRate = total > 0
    ? ((deliveredRead / total) * 100).toFixed(1)
    : "0.0";

  const statCards = [
    { label: "Total Messages", value: totals.total || 0, color: "bg-blue-500", icon: "📊" },
    { label: "Delivered", value: totals.delivered || 0, color: "bg-green-500", icon: "✅" },
    { label: "Read", value: totals.read_count || 0, color: "bg-purple-500", icon: "👁️" },
    { label: "Failed", value: totals.failed || 0, color: "bg-red-500", icon: "❌" },
    { label: "Pending", value: totals.pending || 0, color: "bg-yellow-500", icon: "⏳" },
    { label: "Success Rate", value: `${successRate}%`, color: "bg-teal-500", icon: "📈" },
  ];

  // Calculate max for chart scaling
  const maxDaily = Math.max(...dailyStats.map(d => d.total), 1);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 p-3 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <h1 className="text-xl md:text-3xl font-extrabold text-gray-900 mb-4 md:mb-8">Message Analytics</h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {statCards.map((card, i) => (
                <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-2xl mb-2">{card.icon}</div>
                  <p className="text-xs text-gray-400 font-bold uppercase">{card.label}</p>
                  <p className="text-2xl font-black text-gray-800 mt-1">{card.value}</p>
                </div>
              ))}
            </div>

            {/* Chart - Last 7 Days */}
            <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 mb-8">
              <h2 className="text-lg font-bold text-gray-800 mb-6">Last 7 Days</h2>
              <div className="overflow-x-auto">
                <div className="min-w-[520px] flex items-end gap-2 h-48">
                {dailyStats.length === 0 ? (
                  <p className="text-gray-400 w-full text-center">No data available</p>
                ) : (
                  dailyStats.map((day, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex flex-col gap-1" style={{ height: "160px" }}>
                        {/* Success bar */}
                        <div 
                          className="w-full bg-green-500 rounded-t transition-all"
                          style={{ height: `${(day.success / maxDaily) * 100}%` }}
                          title={`Delivered/Read: ${day.success}`}
                        />
                        {/* Failed bar */}
                        <div 
                          className="w-full bg-red-400 rounded-b transition-all"
                          style={{ height: `${(day.failed / maxDaily) * 100}%` }}
                          title={`Failed: ${day.failed}`}
                        />
                      </div>
                      <span className="text-[10px] text-gray-400 font-bold">
                        {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                      </span>
                    </div>
                  ))
                )}
                </div>
              </div>
              <div className="flex justify-center gap-6 mt-4 text-xs">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded" /> Success
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-400 rounded" /> Failed
                </span>
              </div>
            </div>

            {/* Top Users */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <div className="p-4 md:p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">Top Users by Messages</h2>
              </div>
              <table className="min-w-[760px] w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="p-4 text-left">User</th>
                    <th className="p-4 text-right">Messages</th>
                    <th className="p-4 text-right">Failed</th>
                    <th className="p-4 text-right">Success Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-gray-400">No users found</td>
                    </tr>
                  ) : (
                    topUsers.map((user, i) => {
                      const deliveredReadCount = Number(user.delivered_read_count || 0);
                      const rate = user.message_count > 0 
                        ? ((deliveredReadCount / user.message_count) * 100).toFixed(0)
                        : 0;
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-600">
                                {i + 1}
                              </span>
                              <div>
                                <p className="font-bold text-gray-800">{user.name || "N/A"}</p>
                                <p className="text-xs text-gray-400">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-right font-bold text-gray-800">{user.message_count}</td>
                          <td className="p-4 text-right text-red-500 font-bold">{user.failed_count}</td>
                          <td className="p-4 text-right">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              rate >= 90 ? "bg-green-100 text-green-700" :
                              rate >= 70 ? "bg-yellow-100 text-yellow-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
